/**
 * Test-account review queue.
 *
 * A human confirms every candidate. Confirming quarantines the account — it is
 * reversible, and it deletes nothing. Every row and every S3 object is
 * preserved; the account is simply excluded from the member list and the KPIs.
 *
 * The screen is deliberately evidence-first: an admin should be able to see
 * why an account was flagged, and what would be lost if they were wrong,
 * without leaving the row.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Button, Chip, Alert, Checkbox, CircularProgress, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tabs, Tab,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { addNotification } from '../../store/uiSlice';
import { apiService } from '../../services/api';
import MemberLocation, { MemberLocationFields } from '../common/MemberLocation';

const DEEP_TEAL = '#1A5F7A';
const BURGUNDY = '#7D1935';

interface Candidate extends MemberLocationFields {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  created_at: string;
  subscription_plan: string | null;
  account_class: string;
  test_flag_score: number | null;
  test_flag_reasons: Array<{ signal: string; weight: number; detail?: string }> | null;
  test_quarantined_at: string | null;
  chapter_count: number;
  book_count: number;
  media_count: number;
  payment_count: number;
  apple_sub_count: number;
  play_sub_count: number;
  last_login_at: string | null;
}

const TestAccountReviewPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [tab, setTab] = useState<'flagged' | 'quarantined'>('flagged');
  const [rows, setRows] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [bulkMin, setBulkMin] = useState(80);
  const [playUnavailable, setPlayUnavailable] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; row: Candidate | null }>(
    { open: false, row: null }
  );
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getTestAccountQueue(tab === 'quarantined' ? 'quarantined' : 'flagged');
      setRows(data.candidates || []);
      setBulkMin(data.bulkConfirmMinScore ?? 80);
      setPlayUnavailable(data.playReconciliationAvailable === false);
      setSelected([]);
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to load the review queue',
      }));
    } finally {
      setLoading(false);
    }
  }, [tab, dispatch]);

  useEffect(() => { load(); }, [load]);

  const act = async (
    id: string,
    action: 'confirm' | 'reject' | 'restore',
    why?: string
  ) => {
    setBusyId(id);
    try {
      await apiService.actOnTestAccount(id, action, why);
      dispatch(addNotification({
        type: 'success',
        message:
          action === 'confirm' ? 'Account quarantined. Nothing was deleted — this is reversible.'
          : action === 'reject' ? 'Flag cleared. This account will not be flagged again.'
          : 'Account restored to the member list.',
      }));
      await load();
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: err instanceof Error ? err.message : `Failed to ${action}`,
      }));
    } finally {
      setBusyId(null);
      setConfirmDialog({ open: false, row: null });
      setReason('');
    }
  };

  /** An account with anything of value should never be quarantined casually. */
  const valueAtRisk = (r: Candidate) =>
    r.chapter_count + r.book_count + r.media_count + r.payment_count + r.apple_sub_count + r.play_sub_count;

  const eligibleForBulk = rows.filter((r) => (r.test_flag_score || 0) >= bulkMin);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: DEEP_TEAL, mb: 0.5 }}>
        Test Account Review
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Confirming a candidate quarantines it: hidden from the member list and all KPIs,
        email suppressed, sign-in disabled. <strong>Nothing is deleted, and every action is reversible.</strong>
      </Typography>

      {playUnavailable && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Google Play cannot be checked.</strong> No purchase tokens are stored, so we cannot
          confirm whether any of these accounts paid through Google Play. That is <em>unknown</em>, not
          "no payment" — a paying Android member would look exactly like an empty test account here.
          Check the Play Console before confirming anyone you are unsure about.
        </Alert>
      )}

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Awaiting review" value="flagged" />
        <Tab label="Quarantined" value="quarantined" />
      </Tabs>

      {tab === 'flagged' && selected.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: '#F9F3E9' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2">
              {selected.length} selected — all scoring {bulkMin} or above.
            </Typography>
            <Button
              variant="contained"
              sx={{ bgcolor: BURGUNDY }}
              onClick={async () => {
                try {
                  const res = await apiService.bulkConfirmTestAccounts(selected, 'bulk confirm from review queue');
                  dispatch(addNotification({
                    type: res.refused.length ? 'warning' : 'success',
                    message: `Quarantined ${res.confirmed.length}. Refused ${res.refused.length}.`,
                  }));
                  await load();
                } catch (err) {
                  dispatch(addNotification({ type: 'error', message: 'Bulk confirm failed' }));
                }
              }}
            >
              Quarantine {selected.length} accounts
            </Button>
          </Box>
        </Paper>
      )}

      <Paper>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {tab === 'flagged' && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={eligibleForBulk.length > 0 && selected.length === eligibleForBulk.length}
                      indeterminate={selected.length > 0 && selected.length < eligibleForBulk.length}
                      onChange={(e) =>
                        setSelected(e.target.checked ? eligibleForBulk.map((r) => r.id) : [])
                      }
                    />
                  </TableCell>
                )}
                <TableCell>Member</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Joined</TableCell>
                <TableCell>Last login</TableCell>
                <TableCell>What exists</TableCell>
                <TableCell>Score</TableCell>
                <TableCell>Why flagged</TableCell>
                <TableCell align="right">Decision</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {tab === 'flagged' ? 'No accounts awaiting review.' : 'No quarantined accounts.'}
                  </TableCell>
                </TableRow>
              ) : rows.map((r) => {
                const risk = valueAtRisk(r);
                return (
                  <TableRow key={r.id} hover>
                    {tab === 'flagged' && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          disabled={(r.test_flag_score || 0) < bulkMin}
                          checked={selected.includes(r.id)}
                          onChange={(e) =>
                            setSelected((prev) =>
                              e.target.checked ? [...prev, r.id] : prev.filter((x) => x !== r.id))
                          }
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {[r.first_name, r.last_name].filter(Boolean).join(' ') || '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{r.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <MemberLocation {...r} compact />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{new Date(r.created_at).toLocaleDateString()}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {r.last_login_at ? new Date(r.last_login_at).toLocaleDateString() : 'never'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {risk === 0 ? (
                        <Typography variant="caption" color="text.disabled">empty</Typography>
                      ) : (
                        <Tooltip title="This account holds real member data. Quarantine hides it; it is never deleted.">
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {r.chapter_count > 0 && <Chip size="small" label={`${r.chapter_count} ch`} />}
                            {r.book_count > 0 && <Chip size="small" color="warning" label={`${r.book_count} book`} />}
                            {r.media_count > 0 && <Chip size="small" label={`${r.media_count} media`} />}
                            {(r.payment_count + r.apple_sub_count + r.play_sub_count) > 0 && (
                              <Chip size="small" color="error" label="PAID" />
                            )}
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={r.test_flag_score ?? '—'} sx={{ bgcolor: DEEP_TEAL, color: '#fff', fontWeight: 700 }} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Typography variant="caption" color="text.secondary">
                        {(r.test_flag_reasons || []).map((x) => x.signal).join(', ') || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {tab === 'flagged' ? (
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            size="small" variant="outlined"
                            disabled={busyId === r.id}
                            onClick={() => act(r.id, 'reject')}
                          >
                            Not a test account
                          </Button>
                          <Button
                            size="small" variant="contained"
                            sx={{ bgcolor: BURGUNDY }}
                            disabled={busyId === r.id}
                            onClick={() => setConfirmDialog({ open: true, row: r })}
                          >
                            Confirm
                          </Button>
                        </Box>
                      ) : (
                        <Button
                          size="small" variant="outlined"
                          disabled={busyId === r.id}
                          onClick={() => act(r.id, 'restore')}
                        >
                          Restore
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, row: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Quarantine this account?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>{confirmDialog.row?.email}</strong> will be hidden from the member list and every
            KPI, its email suppressed, and sign-in disabled.
          </Typography>
          {confirmDialog.row && valueAtRisk(confirmDialog.row) > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This account holds real content ({confirmDialog.row.chapter_count} chapters,{' '}
              {confirmDialog.row.book_count} books, {confirmDialog.row.media_count} media).
              It is preserved, not deleted — but check you have the right account.
            </Alert>
          )}
          <Alert severity="info" sx={{ mb: 2 }}>
            Nothing is deleted. You can restore this account at any time from the Quarantined tab.
          </Alert>
          <TextField
            fullWidth label="Reason (recorded in the audit log)" value={reason}
            onChange={(e) => setReason(e.target.value)} size="small" multiline rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, row: null })}>Cancel</Button>
          <Button
            variant="contained" sx={{ bgcolor: BURGUNDY }}
            onClick={() => confirmDialog.row && act(confirmDialog.row.id, 'confirm', reason)}
          >
            Quarantine
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TestAccountReviewPage;
